import React, { useEffect, useState } from 'react';
import { isBookmarked, toggleBookmark } from '../lib/bookmarks';
import LocationLinks from './LocationLinks';

function formatTime(time) {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
}

function formatDate(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
    });
}

function formatPrice(price) {
    if (!price) return '';
    if (price.amount === 0 || price.description === 'Free') return 'Free';
    if (price.amount > 0) return `$${price.amount} ${price.description || ''}`.trim();
    return price.description || '';
}

const BUSINESS_AVATAR_PREFIX = 'https://tacxuksfiabbdygcfnqr.supabase.co/storage/v1/object/public/media/businesses';

/**
 * EventDetailsDrawer — bottom sheet with the full details for an event.
 * Used from the feed's Details action (fixed to the viewport) and from the
 * story slides' Details action (absolute within the story frame). Shows the
 * hosting business, date/time, price, location, badges and description.
 */
export default function EventDetailsDrawer({ business, event, location, analytics, onClose, position = 'fixed', trackNs = 'story' }) {
    if (!event) return null;

    const eventId = event.eventId || event.id;
    const eventDate = event.eventDate || event.event_date;
    const startTime = event.start_time;
    const endTime = event.end_time;
    const price = event.price;
    const poster = event.poster || null;
    const description = event.description || '';
    const ticketUrl = event.ticketUrl || event.ticket_url || '';
    const rsvpUrl = event.rsvpUrl || event.rsvp_url || '';
    const infoUrl = event.infoUrl || event.info_url || '';
    const over21 = Boolean(event.over21 ?? event.over_21);
    const over18 = Boolean(event.over18 ?? event.over_18);
    const virtual = Boolean(event.virtual);
    const soldOut = Boolean(event.soldOut ?? event.sold_out);
    const goingFast = Boolean(event.goingFast ?? event.going_fast);

    const seriesInstances = Array.isArray(event.seriesInstances)
        ? event.seriesInstances.filter((instance) => instance && instance.id && instance.id !== eventId)
        : [];
    const nextDates = seriesInstances.length > 0
        ? seriesInstances
        : (event.nextSeriesEvents || event.next_series_events || []);
    const isRecurring = nextDates.length > 0;

    const locationName = location?.name || event.locationName || '';
    const locationAddress = location?.address || event.locationAddress || '';
    const locationPhone = location?.phone || event.locationPhone || '';
    const telHref = locationPhone ? `tel:${String(locationPhone).replace(/[^0-9+]/g, '')}` : null;

    const businessName = business?.businessTitle || business?.title || '';
    const businessAvatar = business?.avatarUrl || (
        business?.id && business?.avatar
            ? `${BUSINESS_AVATAR_PREFIX}/${business.id}/${business.avatar}`
            : null
    );
    const businessId = business?.id || business?.businessId || '';
    const businessHref = businessId
        ? (business?.isResource || business?.isresource
            ? `/resources/#resource-${businessId}`
            : `/businesses/${businessId}/`)
        : null;
    const BusinessTag = businessHref ? 'a' : 'div';
    const handleBusinessClick = () => {
        analytics?.trackInteraction(trackNs === 'event' ? 'click_event_business' : 'click_story_business', { businessId });
    };

    const primaryUrl = ticketUrl || rsvpUrl || infoUrl || (eventId ? `/events/${eventId}/` : null);
    const ctaLabel = ticketUrl ? 'Get Tickets' : rsvpUrl ? 'RSVP' : infoUrl ? 'View Event' : 'View Event';
    const ctaExternal = Boolean(ticketUrl || rsvpUrl || infoUrl);

    const [saved, setSaved] = useState(() => isBookmarked('event', eventId));

    useEffect(() => {
        const sync = () => setSaved(isBookmarked('event', eventId));
        window.addEventListener('pw:bookmarks-changed', sync);
        return () => window.removeEventListener('pw:bookmarks-changed', sync);
    }, [eventId]);

    useEffect(() => {
        if (position !== 'fixed' || !document.body) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [position]);

    const handleBookmarkToggle = () => {
        setSaved(toggleBookmark({
            type: 'event',
            id: eventId,
            title: event.title,
            date: eventDate,
            time: startTime,
            charge: formatPrice(price),
        }));
        analytics?.trackInteraction(trackNs === 'event' ? 'click_event_save' : 'click_story_save');
    };

    const handleCta = () => {
        analytics?.trackInteraction(trackNs === 'event' ? 'click_event_cta' : 'click_story_cta');
    };

    return (
        <div className={`${position === 'fixed' ? 'fixed inset-0 z-50' : 'absolute inset-0 z-30'}`} role="dialog" aria-modal="true" aria-label={`${event.title} details`}>
            <div className="absolute inset-0 bg-black/60 animate-fade-in" onClick={onClose} aria-hidden="true" />

            <div
                className={[
                    'absolute bottom-0 bg-white rounded-t-3xl shadow-2xl flex flex-col animate-drawer-up',
                    position === 'fixed'
                        ? 'inset-x-0 h-[90%] md:mx-auto md:top-6 md:bottom-6 md:h-auto md:w-full md:max-w-lg md:rounded-3xl'
                        : 'inset-x-0 h-[90%]',
                ].join(' ')}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-center pt-3 pb-1">
                    <span className="w-10 h-1.5 rounded-full bg-gray-300" />
                </div>

                <div className="flex items-center gap-3 px-5 pt-2 pb-3 border-b border-gray-100">
                    <BusinessTag
                        {...(businessHref ? { href: businessHref, onClick: handleBusinessClick } : {})}
                        className="flex items-center gap-3 min-w-0 flex-1 no-underline text-gray-900 hover:opacity-90 transition-opacity"
                    >
                        {businessAvatar ? (
                            <img src={businessAvatar} alt="" className="w-9 h-9 rounded-full object-cover border border-gray-200" />
                        ) : null}
                        <div className="min-w-0 flex-1">
                            <p className="font-semibold text-gray-900 leading-tight truncate">{businessName}</p>
                            <p className="text-sm text-gray-500 truncate">
                                {formatDate(eventDate)}{startTime ? ` · ${formatTime(startTime)}` : ''}
                                {locationName ? ` · ${locationName}` : ''}
                            </p>
                        </div>
                    </BusinessTag>
                    <button
                        type="button"
                        onClick={handleBookmarkToggle}
                        aria-pressed={saved}
                        aria-label={saved ? 'Unsave event' : 'Save event'}
                        className={`p-2 rounded-full transition-colors ${saved ? 'text-brand-700 hover:bg-brand-50' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'}`}
                    >
                        <svg className="w-5 h-5" fill={saved ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
                        aria-label="Close details"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
                    {/* Poster — edge-to-edge (breaks out of the horizontal content padding), natural height, no crop */}
                    {poster && (
                        <div className="-mx-5 overflow-hidden -mt-1 mb-4">
                            <img src={poster} alt="" className="w-full h-auto object-contain" loading="lazy" />
                        </div>
                    )}

                    <h2 className="text-2xl font-bold text-gray-900 leading-snug mb-3">{event.title}</h2>

                    <div className="flex flex-wrap gap-2 mb-4">
                        {formatPrice(price) && (
                            <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-brand-100 text-brand-800 rounded-full">{formatPrice(price)}</span>
                        )}
                        {over21 && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded-full">21+</span>}
                        {over18 && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded-full">18+</span>}
                        {virtual && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-emerald-100 text-emerald-800 rounded-full">Virtual</span>}
                        {soldOut && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-red-100 text-red-800 rounded-full">Sold Out</span>}
                        {goingFast && !soldOut && <span className="inline-block px-2.5 py-1 text-xs font-semibold bg-orange-100 text-orange-800 rounded-full">Going Fast</span>}
                    </div>

                    {/* Location block */}
                    {locationName || locationAddress ? (
                        <div className="mb-4">
                            <div className="flex items-start gap-2.5 rounded-xl bg-gray-50 border border-gray-100 p-3">
                                <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                                </svg>
                                <div className="min-w-0">
                                    {locationName && <p className="font-semibold text-gray-900 text-sm leading-snug">{locationName}</p>}
                                    {locationAddress && <p className="text-sm text-gray-600 leading-snug">{locationAddress}</p>}
                                    {telHref && (
                                        <a href={telHref} className="text-sm font-medium text-brand-700 hover:text-brand-800 no-underline leading-snug">{locationPhone}</a>
                                    )}
                                </div>
                            </div>
                            <LocationLinks address={locationAddress} name={locationName} className="mt-2 px-3" />
                        </div>
                    ) : null}

                    {description ? (
                        <div>
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">About this event</h3>
                            <p className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-line">{description}</p>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">No additional details available.</p>
                    )}

                    <div className="mt-5 pt-4 border-t border-gray-100 space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Date</span>
                            <span className="font-medium text-gray-900">{formatDate(eventDate)}</span>
                        </div>
                        {startTime && (
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Time</span>
                                <span className="font-medium text-gray-900">
                                    {formatTime(startTime)}{endTime ? ` – ${formatTime(endTime)}` : ''}
                                </span>
                            </div>
                        )}
                        {formatPrice(price) && (
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Price</span>
                                <span className="font-medium text-gray-900">{formatPrice(price)}</span>
                            </div>
                        )}
                    </div>

                    {isRecurring && (
                        <div className="mt-4 rounded-xl border border-brand-100 bg-brand-50/70 p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 flex-shrink-0 text-brand-700" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V9.347M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                                </svg>
                                <p className="text-sm font-semibold text-brand-800">Recurring event</p>
                            </div>
                            <p className="text-xs text-gray-600 mb-2">Also happening on:</p>
                            <ul className="space-y-1">
                                {nextDates.slice(0, 5).map((next, index) => (
                                    <li key={next.eventId || next.id || index} className="text-sm text-gray-700">
                                        {(next.eventId || next.id) ? (
                                            <a
                                                href={`/events/${next.eventId || next.id}/`}
                                                className="text-brand-700 hover:text-brand-800 font-medium no-underline"
                                            >
                                                {formatDate(next.date)}
                                            </a>
                                        ) : (
                                            formatDate(next.date)
                                        )}
                                    </li>
                                ))}
                            </ul>
                            {nextDates.length > 5 && (
                                <p className="text-xs text-gray-500 mt-1">+{nextDates.length - 5} more dates</p>
                            )}
                        </div>
                    )}
                </div>

                {primaryUrl && (
                    <div className="px-5 pt-3 pb-5 border-t border-gray-100">
                        <a
                            href={primaryUrl}
                            target={ctaExternal ? '_blank' : undefined}
                            rel={ctaExternal ? 'noopener noreferrer' : undefined}
                            onClick={handleCta}
                            className="post-cta w-full"
                        >
                            {ctaLabel}
                            {ctaExternal && (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                </svg>
                            )}
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}